import React, { h, createContext, ComponentChildren } from 'preact'
import { useContext, useEffect, useState } from 'preact/hooks'
import axios from 'axios'
import { IntegrationType, MaintenantState, Tenant } from 'grosella/types'
import { WidgetConfigContext } from './widget-config-provider'
import { Manifest } from 'grosella/manifest'
import { isLocalDev } from 'grosella/utils'

export const TenantContext = createContext<Tenant>({} as Tenant)

interface Props {
    children: ComponentChildren
}

/**
 * Provider to put the `primaryColor` to use for the tenant in context.
 *
 * In the normal scenario, the primaryColor is fetched from DragonFruit
 * based on the urlPrefix.
 * However, if we are in maintenance mode (see https://www.notion.so/logixboard/Scheduled-Application-Maintenance-3a8d6948a461455392bcd4322c89bbed
 * for more details), dragonFruit will be unavailable, and the primaryColor needs
 * to be taken from the `maintenance-state.json` file instead.
 * This would take priority.
 */
export const TenantProvider: React.FunctionalComponent<Props> = ({
    children,
}: Props) => {
    const widgetConfig = useContext(WidgetConfigContext)
    const [tenant, setTenant] = useState<Tenant>(undefined)

    const fetchMaintenanceState = async (): Promise<MaintenantState> => {
        // For local dev convenience, expect localhost:8080.
        // When testing locally, serve the maintenance-state.json file from local http server.
        // Eg.
        // `http-server --cors -c10 .`
        // and comment out the test.
        const host = isLocalDev() ? 'http://localhost:3000' : ''
        if (!isLocalDev()) {
            try {
                const response = await axios.get(
                    `${host}/maintenance/${widgetConfig.urlPrefix}/maintenance-state.json`
                )
                if (response.status === 200) {
                    return response.data
                }
            } catch (err) {
                // Ignore
            }
        }
        return undefined
    }

    const fetchFromDragonFruit = async (): Promise<Tenant> => {
        const response = await axios.post(
            `${Manifest.services.dragonFruit}/anon/graphql`,
            {
                operationName: 'BrandingByUrlPrefix',
                query: `
            query BrandingByUrlPrefix($urlPrefix: String!) {
                brandByUrlPrefix(urlPrefix: $urlPrefix) {
                    primaryColor
                    tenant {
                        integrationType
                        id
                    }
                    brandDomains(condition: { isMain: true }) {
                        nodes {
                            id
                            domainName
                            isMain
                        }
                    }
                }
            }
        `,
                variables: {
                    urlPrefix: widgetConfig.urlPrefix,
                },
            }
        )

        const { brandByUrlPrefix } = response.data.data

        const domainName =
            brandByUrlPrefix.brandDomains.nodes[0].domainName ||
            `${widgetConfig.urlPrefix}.logixboard.com`

        return {
            primaryColor: `#${brandByUrlPrefix.primaryColor}`,
            integrationType: `${brandByUrlPrefix.tenant.integrationType}`,
            id: `${brandByUrlPrefix.tenant.id}`,
            domainName,
        }
    }

    useEffect(() => {
        // Fetch maintenanceState first.
        fetchMaintenanceState().then((maintenanceState) => {
            if (maintenanceState?.maintenanceActive) {
                // If maintenanceState is active avoid any call to dragonFruit
                // instead pull primaryColor from the tenantInfo.
                const tenant = maintenanceState.tenantInfo || {
                    primaryColor: '#2ECCBC',
                    integrationType: IntegrationType.CARGOWISE,
                    id: '',
                    domainName: '',
                }
                setTenant(tenant)
            } else {
                fetchFromDragonFruit().then(setTenant)
            }
        })
    }, [])
    return (
        <TenantContext.Provider value={tenant}>
            {tenant && children}
        </TenantContext.Provider>
    )
}
